export interface CompositionEntry {
  shape: string
  width?: number
  height?: number
  [key: string]: unknown
}

// Form-state shape: width/height always present (nullable while the user
// clears an input; coerced back to numbers on submit).
export interface FormCompositionEntry {
  shape: string
  width: number | null
  height: number | null
  [key: string]: unknown
}

// dimensions.composition is untyped JSON and is stored inconsistently:
// sometimes a real array, sometimes a JSON-encoded string (see
// SofaDrawingElements/SofaElements/COMPOSITE.tsx parse branch).
export function parseComposition(composition: unknown): CompositionEntry[] {
  let value = composition
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return []
    }
  }
  if (!Array.isArray(value)) return []
  return value.filter(
    (entry): entry is CompositionEntry =>
      typeof entry?.shape === 'string' && entry.shape.length > 0,
  )
}

const toNumber = (value: unknown): number => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

// null iff raw is null/undefined — non-COMPOSITE modules must keep storing
// null, not [].
// Migration note: stage-2 builds briefly wrote member fields under a nested
// `dimensions` sub-object; the ecosystem contract (ERP importer, all COMPOSITE
// renderer copies) is FLAT keys on the entry. Flatten on load — nested values
// win over stale flat ones — except the footprint width/height, which always
// come from the top level (a nested `height` was the unrelated furniture
// height field and must not clobber the footprint).
export function toFormComposition(raw: unknown): FormCompositionEntry[] | null {
  if (raw === null || raw === undefined) return null
  return parseComposition(raw).map(entry => {
    const { dimensions, ...flat } = entry as CompositionEntry & {
      dimensions?: Record<string, unknown> | null
    }
    return {
      ...flat,
      ...(typeof dimensions === 'object' && dimensions !== null
        ? dimensions
        : {}),
      shape: entry.shape,
      width: toNumber(entry.width),
      height: toNumber(entry.height),
    }
  })
}
